import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faUsers,
  faReply,
  faPaperPlane,
  faSpinner,
  faCalendarAlt,
  faSyncAlt,
  faFileExcel,
  faChartBar,
  faFilter,
  faRobot,
  faUserShield,
  faServer,
  faDollarSign,
  faCoins
} from "@fortawesome/free-solid-svg-icons";
import { useStatisticsService } from '../services/statistics.service';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Avatar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ApexCharts from 'apexcharts';

// Styled components matching current system styles
const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #008069 0%, #00a884 100%)',
  color: 'white',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  border: '1px solid #e9edef',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef',
  color: '#111b21',
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '16px 24px',
  border: '1px solid #e9edef',
  marginBottom: '24px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
}));

export default function ReportesMensajes() {
  const statisticsService = useStatisticsService();

  // Initial State: July 2026 is current time. Default range 01-01-2026 to 10-07-2026
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-07-10');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [campaignTab, setCampaignTab] = useState('excel');


  const systemKeys = ['generated by system', 'admin', 'system/automated'];

  const systemStats = stats?.overall?.messagesByEjecutivo?.reduce((acc, curr) => {
    const name = curr.ejecutivo?.toLowerCase() || '';
    if (systemKeys.includes(name)) {
      acc[name] = curr.sentCount;
    }
    return acc;
  }, { 'generated by system': 0, 'admin': 0, 'system/automated': 0 }) || { 'generated by system': 0, 'admin': 0, 'system/automated': 0 };

  const humanExecutives = stats?.overall?.messagesByEjecutivo?.filter(e => {
    const name = e.ejecutivo?.toLowerCase() || '';
    return !systemKeys.includes(name);
  }) || [];


  // Helper to convert YYYY-MM-DD input date to DD-MM-YYYY API format
  const formatToAPIDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const loadStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const formattedStart = formatToAPIDate(startDate);
      const formattedEnd = formatToAPIDate(endDate);
      const data = await statisticsService.getMessageStatistics(period, formattedStart, formattedEnd);
      setStats(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Error al cargar las estadísticas. Por favor intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Load stats on mount
  useEffect(() => {
    loadStatistics();
  }, []);

  // ApexCharts rendering logic
  useEffect(() => {
    if (!stats || loading) return;

    let sentRecChart;
    let execChart;
    let campaignChart;
    let templateChart;
    let costsChart;


    // 1. Sent vs Received Donut Chart
    const sentRecEl = document.getElementById("sent-rec-chart");
    if (sentRecEl) {
      const options = {
        chart: {
          type: 'donut',
          height: 320,
          fontFamily: 'Inter, sans-serif',
          id: 'sent-rec-chart-id'
        },
        series: [stats.overall?.messagesSentBySystem || 0, stats.overall?.messagesReceived || 0],
        labels: ['Enviados por Sistema', 'Recibidos de Contactos'],
        colors: ['#008069', '#34b7f1'],
        legend: {
          position: 'bottom',
          fontSize: '13px',
          fontWeight: 500,
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return opts.w.config.series[opts.seriesIndex];
          }
        },
        tooltip: {
          y: {
            formatter: (val) => `${val} mensajes`
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total Mensajes',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111b21',
                  formatter: () => stats.overall?.totalMessages || 0
                }
              }
            }
          }
        }
      };
      sentRecChart = new ApexCharts(sentRecEl, options);
      sentRecChart.render();
    }

    // 2. Template Donut Chart
    const templateEl = document.getElementById("templates-chart");
    if (templateEl && stats.overall?.templateStats && stats.overall.templateStats.length > 0) {
      const options = {
        chart: {
          type: 'donut',
          height: 320,
          fontFamily: 'Inter, sans-serif',
          id: 'templates-chart-id'
        },
        series: stats.overall.templateStats.map(t => t.count || 0),
        labels: stats.overall.templateStats.map(t => t.templateName || 'Sin Nombre'),
        colors: ['#008069', '#34b7f1', '#f59e0b', '#ef4444', '#10b981', '#3b82f6'],
        legend: {
          position: 'bottom',
          fontSize: '13px',
          fontWeight: 500,
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return opts.w.config.series[opts.seriesIndex];
          }
        },
        tooltip: {
          y: {
            formatter: (val) => `${val} mensajes`
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total Plantillas',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111b21',
                  formatter: () => stats.overall?.templateStats?.reduce((acc, t) => acc + (t.count || 0), 0) || 0
                }
              }
            }
          }
        }
      };
      templateChart = new ApexCharts(templateEl, options);
      templateChart.render();
    }

    // 3. Executives Bar Chart
    const execEl = document.getElementById("executives-chart");
    if (execEl && humanExecutives.length > 0) {
      // Sort executivos descending by sent count
      const sortedEjecutivos = [...humanExecutives]
        .sort((a, b) => b.sentCount - a.sentCount)
        .slice(0, 10); // Show top 10


      const options = {
        chart: {
          type: 'bar',
          height: 320,
          fontFamily: 'Inter, sans-serif',
          id: 'executives-chart-id',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '60%',
            borderRadius: 4,
            distributed: false
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => val
        },
        series: [{
          name: 'Mensajes Enviados',
          data: sortedEjecutivos.map(e => e.sentCount)
        }],
        xaxis: {
          categories: sortedEjecutivos.map(e => e.ejecutivo || 'Desconocido'),
          labels: {
            style: {
              colors: '#667085',
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: '#111b21',
              fontSize: '12px',
              fontWeight: 500
            }
          }
        },
        colors: ['#008069'],
        tooltip: {
          y: {
            formatter: (val) => `${val} mensajes`
          }
        }
      };
      execChart = new ApexCharts(execEl, options);
      execChart.render();
    }

    // 4. Excel & Automated Campaigns Stacked Bar Chart
    const campaignEl = document.getElementById("campaigns-chart");
    if (campaignEl) {
      if (campaignTab === 'excel' && stats.overall?.excelCampaigns && stats.overall.excelCampaigns.length > 0) {
        const campaigns = stats.overall.excelCampaigns.slice(0, 8); // Display top 8 campaigns

        const options = {
          chart: {
            type: 'bar',
            height: 320,
            stacked: true,
            fontFamily: 'Inter, sans-serif',
            id: 'campaigns-chart-id',
            toolbar: { show: false }
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: '45%',
              borderRadius: 4
            },
          },
          series: [
            {
              name: 'Enviados',
              data: campaigns.map(c => c.sent || 0)
            },
            {
              name: 'Pendientes',
              data: campaigns.map(c => c.pending || 0)
            },
            {
              name: 'Fallidos',
              data: campaigns.map(c => (c.failed_send || 0) + (c.failed_delivery || 0))
            },
            {
              name: 'Respondidos',
              data: campaigns.map(c => c.replied || 0)
            }
          ],
          xaxis: {
            categories: campaigns.map(c => {
              const name = c.fileName || '';
              // Truncate to just the filename (before space / timestamp)
              const cleanName = name.split('.xlsx')[0] || 'Campaña';
              return cleanName.length > 15 ? cleanName.substring(0, 15) + '...' : cleanName;
            }),
            labels: {
              style: {
                colors: '#667085',
                fontSize: '11px'
              }
            }
          },
          colors: ['#10b981', '#f59e0b', '#ef4444', '#3b82f6'],
          legend: {
            position: 'top',
            fontSize: '13px',
            fontWeight: 500,
          },
          tooltip: {
            y: {
              formatter: (val) => `${val} mensajes`
            }
          }
        };
        campaignChart = new ApexCharts(campaignEl, options);
        campaignChart.render();
      } else if (campaignTab === 'automated' && stats.overall?.automatedCampaigns && stats.overall.automatedCampaigns.length > 0) {
        const campaigns = stats.overall.automatedCampaigns.slice(0, 8); // Display top 8 campaigns

        const options = {
          chart: {
            type: 'bar',
            height: 320,
            stacked: true,
            fontFamily: 'Inter, sans-serif',
            id: 'campaigns-chart-id',
            toolbar: { show: false }
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: '45%',
              borderRadius: 4
            },
          },
          series: [
            {
              name: 'Enviados',
              data: campaigns.map(c => c.sent || 0)
            },
            {
              name: 'Omitidos',
              data: campaigns.map(c => c.skipped || 0)
            },
            {
              name: 'Fallidos',
              data: campaigns.map(c => (c.failed_send || 0) + (c.failed_delivery || 0))
            },
            {
              name: 'Respondidos',
              data: campaigns.map(c => c.replied || 0)
            }
          ],
          xaxis: {
            categories: campaigns.map(c => {
              const name = c.name || '';
              // Remove Automatizacion prefix
              const cleanName = name.replace('Automatizacion:', '').trim();
              return cleanName.length > 15 ? cleanName.substring(0, 15) + '...' : cleanName;
            }),
            labels: {
              style: {
                colors: '#667085',
                fontSize: '11px'
              }
            }
          },
          colors: ['#10b981', '#9ca3af', '#ef4444', '#3b82f6'],
          legend: {
            position: 'top',
            fontSize: '13px',
            fontWeight: 500,
          },
          tooltip: {
            y: {
              formatter: (val) => `${val} mensajes`
            }
          }
        };
        campaignChart = new ApexCharts(campaignEl, options);
        campaignChart.render();
      }
    }


    // 5. Costs Donut Chart
    const costsEl = document.getElementById("costs-chart");
    if (costsEl && stats.overall?.costs) {
      const costsObj = stats.overall.costs;
      const categories = ['marketing', 'utility', 'authentication', 'service'];
      const dataSeries = categories.map(cat => costsObj[cat]?.cost || 0);
      const labels = ['Marketing', 'Utility', 'Authentication', 'Service'];
      
      const options = {
        chart: {
          type: 'donut',
          height: 320,
          fontFamily: 'Inter, sans-serif',
          id: 'costs-chart-id'
        },
        series: dataSeries,
        labels: labels,
        colors: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'],
        legend: {
          position: 'bottom',
          fontSize: '13px',
          fontWeight: 500,
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return `$${opts.w.config.series[opts.seriesIndex].toFixed(2)}`;
          }
        },
        tooltip: {
          y: {
            formatter: (val) => `$${val.toFixed(4)}`
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Costo Total',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111b21',
                  formatter: () => `$${costsObj.totalCost?.toFixed(2) || '0.00'}`
                }
              }
            }
          }
        }
      };
      costsChart = new ApexCharts(costsEl, options);
      costsChart.render();
    }

    return () => {
      if (sentRecChart) sentRecChart.destroy();
      if (execChart) execChart.destroy();
      if (campaignChart) campaignChart.destroy();
      if (templateChart) templateChart.destroy();
      if (costsChart) costsChart.destroy();
    };

  }, [stats, loading, campaignTab]);

  return (
    <Box sx={{ p: 4, maxWidth: '1600px', margin: '0 auto', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Title Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#111b21', mb: 1 }}>
          Reportes de Mensajes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visualiza estadísticas y rendimiento de mensajería, ejecutivos y campañas activas.
        </Typography>
      </Box>

      {/* Filters Container */}
      <FilterContainer>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="period-select-label">Período</InputLabel>
              <Select
                labelId="period-select-label"
                id="period-select"
                value={period}
                label="Período"
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="day">Día</MenuItem>
                <MenuItem value="week">Semana</MenuItem>
                <MenuItem value="month">Mes</MenuItem>
                <MenuItem value="year">Año</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Fecha Inicio"
              type="date"
              fullWidth
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Fecha Fin"
              type="date"
              fullWidth
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3} sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={loadStatistics}
              disabled={loading}
              startIcon={loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFilter} />}
              sx={{
                backgroundColor: '#008069',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                height: '40px',
                '&:hover': {
                  backgroundColor: '#006d5a',
                },
              }}
            >
              Filtrar
            </Button>
            <Button
              variant="outlined"
              onClick={loadStatistics}
              disabled={loading}
              sx={{
                borderColor: '#e9edef',
                color: '#111b21',
                borderRadius: '8px',
                minWidth: '48px',
                height: '40px',
                '&:hover': {
                  borderColor: '#008069',
                  backgroundColor: '#e8f5e8',
                },
              }}
            >
              <FontAwesomeIcon icon={faSyncAlt} className={loading ? 'fa-spin' : ''} />
            </Button>
          </Grid>
        </Grid>
      </FilterContainer>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#008069' }} />
        </Box>
      )}

      {!loading && stats && (
        <>
          {/* Top Metrics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Metric 1 */}
            <Grid item xs={12} sm={6} md={2.4}>
              <StyledCard>
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                        {stats.overall?.totalMessages || 0}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        Mensajes Totales
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <FontAwesomeIcon icon={faEnvelope} size="lg" />
                    </Avatar>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.8 }}>
                    Enviados: {stats.overall?.messagesSentBySystem || 0} | Recibidos: {stats.overall?.messagesReceived || 0}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Metric 2 */}
            <Grid item xs={12} sm={6} md={2.4}>
              <StyledCard sx={{ background: 'linear-gradient(135deg, #2b6cb0 0%, #3182ce 100%)' }}>
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                        {stats.overall?.totalAsegurados || 0}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        Total Asegurados
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <FontAwesomeIcon icon={faUsers} size="lg" />
                    </Avatar>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.8 }}>
                    Activos: {stats.overall?.activeAsegurados || 0} | Nuevos creados: {stats.overall?.newAseguradosCreated || 0}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Metric 3 */}
            <Grid item xs={12} sm={6} md={2.4}>
              <StyledCard sx={{ background: 'linear-gradient(135deg, #d69e2e 0%, #ecc94b 100%)' }}>
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                        {stats.overall?.relationSentToReceived?.ratio?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        Relación Env./Rec.
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <FontAwesomeIcon icon={faPaperPlane} size="lg" />
                    </Avatar>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.8 }}>
                    Mensajes enviados por cada mensaje recibido.
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Metric 4 */}
            <Grid item xs={12} sm={6} md={2.4}>
              <StyledCard sx={{ background: 'linear-gradient(135deg, #b7791f 0%, #dd6b20 100%)' }}>
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                        {stats.overall?.relationSentToReceived?.contactReplyRate
                          ? `${(stats.overall.relationSentToReceived.contactReplyRate * 100).toFixed(2)}%`
                          : '0.00%'}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        Tasa de Respuesta
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <FontAwesomeIcon icon={faReply} size="lg" />
                    </Avatar>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.8 }}>
                    Porcentaje de clientes que responden.
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Metric 5 */}
            <Grid item xs={12} sm={6} md={2.4}>
              <StyledCard sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                <CardContent sx={{ p: '24px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                        {stats.overall?.costs?.totalCost !== undefined
                          ? `$${stats.overall.costs.totalCost.toFixed(2)}`
                          : '$0.00'}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        Costo Total (USD)
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <FontAwesomeIcon icon={faDollarSign} size="lg" />
                    </Avatar>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.8 }}>
                    Cargos: {
                      (stats.overall.costs?.marketing?.count || 0) +
                      (stats.overall.costs?.utility?.count || 0) +
                      (stats.overall.costs?.authentication?.count || 0) +
                      (stats.overall.costs?.service?.count || 0)
                    } | Gratuitos: {stats.overall.costs?.free?.totalCount || 0}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Chart 1: Donut */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2 }}>
                    Canales de Mensajes
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px' }}>
                    <div id="sent-rec-chart" style={{ width: '100%' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Chart 2: Donut (Templates) */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2 }}>
                    Uso de Plantillas (Templates)
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px' }}>
                    {stats.overall?.templateStats && stats.overall.templateStats.length > 0 ? (
                      <div id="templates-chart" style={{ width: '100%' }} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay datos de plantillas en este rango.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Chart 3: Donut (Costs) */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2 }}>
                    Distribución de Costos (USD)
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px' }}>
                    {stats.overall?.costs?.totalCost > 0 ? (
                      <div id="costs-chart" style={{ width: '100%' }} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay datos de costos en este rango.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Chart 3: Executives Bar */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2 }}>
                    Top 10 Ejecutivos por Mensajes Enviados
                  </Typography>
                  <div id="executives-chart" />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Campaigns Chart Row */}
          {((stats.overall?.excelCampaigns && stats.overall.excelCampaigns.length > 0) ||
            (stats.overall?.automatedCampaigns && stats.overall.automatedCampaigns.length > 0)) && (
            <Card sx={{ borderRadius: '16px', border: '1px solid #e9edef', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)', mb: 4 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21' }}>
                    {campaignTab === 'excel' ? 'Desglose de Campañas de Excel (Enviados, Pendientes, Fallidos, Respondidos)' : 'Desglose de Campañas Automatizadas (Enviados, Omitidos, Fallidos, Respondidos)'}
                  </Typography>
                  <Box sx={{ display: 'flex', bgcolor: '#f0f2f5', borderRadius: '8px', p: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => setCampaignTab('excel')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '6px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        minWidth: '100px',
                        color: campaignTab === 'excel' ? '#008069' : '#667085',
                        bgcolor: campaignTab === 'excel' ? '#ffffff' : 'transparent',
                        boxShadow: campaignTab === 'excel' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        '&:hover': { bgcolor: campaignTab === 'excel' ? '#ffffff' : '#e4e6eb' }
                      }}
                    >
                      Campañas Excel
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setCampaignTab('automated')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '6px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        minWidth: '100px',
                        color: campaignTab === 'automated' ? '#008069' : '#667085',
                        bgcolor: campaignTab === 'automated' ? '#ffffff' : 'transparent',
                        boxShadow: campaignTab === 'automated' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        '&:hover': { bgcolor: campaignTab === 'automated' ? '#ffffff' : '#e4e6eb' }
                      }}
                    >
                      Automatizadas
                    </Button>
                  </Box>
                </Box>
                <div id="campaigns-chart" />
              </CardContent>
            </Card>
          )}

          {/* Data Tables Container */}
          <Grid container spacing={3}>
            {/* Table 1: Executives Detail */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faChartBar} style={{ color: '#008069' }} />
                Estadísticas de Ejecutivos
              </Typography>
              <StyledTableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Ejecutivo</StyledTableCell>
                      <StyledTableCell align="right">Enviados</StyledTableCell>
                      <StyledTableCell align="right">Total</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {humanExecutives?.map((row, index) => (
                      <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 500, py: 1.5 }}>
                          {row.ejecutivo}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>{row.sentCount}</TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>{row.totalCount}</TableCell>
                      </TableRow>
                    ))}
                    {(!humanExecutives || humanExecutives.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No hay datos de ejecutivos.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </Grid>

            {/* Table 2: System & Admin Stats */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faServer} style={{ color: '#008069' }} />
                Mensajes de Sistema y Admin
              </Typography>
              <Card sx={{ borderRadius: '12px', border: '1px solid #e9edef', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', minHeight: '300px' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Item 1 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f2f5', pb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#e8f5e8', color: '#008069', width: 44, height: 44 }}>
                          <FontAwesomeIcon icon={faRobot} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111b21' }}>
                            Generados por Sistema
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Envíos automáticos del backend
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#008069' }}>
                        {systemStats['generated by system']}
                      </Typography>
                    </Box>

                    {/* Item 2 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f2f5', pb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1e88e5', width: 44, height: 44 }}>
                          <FontAwesomeIcon icon={faServer} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111b21' }}>
                            Automatizados
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Respuestas automáticas / triggers
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e88e5' }}>
                        {systemStats['system/automated']}
                      </Typography>
                    </Box>

                    {/* Item 3 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#ede7f6', color: '#5e35b1', width: 44, height: 44 }}>
                          <FontAwesomeIcon icon={faUserShield} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111b21' }}>
                            Administrador (Admin)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mensajes enviados por cuenta admin
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#5e35b1' }}>
                        {systemStats['admin']}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Table 3: Templates Detail */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faChartBar} style={{ color: '#008069' }} />
                Uso de Plantillas (Templates)
              </Typography>
              <StyledTableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Plantilla</StyledTableCell>
                      <StyledTableCell align="right">Mensajes Enviados</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.overall?.templateStats?.map((row, index) => (
                      <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 500, py: 1.5 }}>
                          {row.templateName}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>{row.count}</TableCell>
                      </TableRow>
                    ))}
                    {(!stats.overall?.templateStats || stats.overall.templateStats.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No hay datos de plantillas.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </Grid>
            {/* Table 4: Campaigns Detail */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FontAwesomeIcon icon={campaignTab === 'excel' ? faFileExcel : faChartBar} style={{ color: '#008069' }} />
                  {campaignTab === 'excel' ? 'Campañas de Excel Cargadas' : 'Rendimiento de Automatizaciones'}
                </Typography>
              </Box>

              {campaignTab === 'excel' ? (
                <StyledTableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Archivo de Campaña</StyledTableCell>
                        <StyledTableCell align="right">Total</StyledTableCell>
                        <StyledTableCell align="right">Enviados</StyledTableCell>
                        <StyledTableCell align="right">Pendientes</StyledTableCell>
                        <StyledTableCell align="right">Fallidos</StyledTableCell>
                        <StyledTableCell align="right">Resp.</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.overall?.excelCampaigns?.map((row, index) => {
                        const cleanName = row.fileName || '';
                        // Truncate to just the filename part
                        const parts = cleanName.split('.xlsx');
                        const displayTitle = parts[0] ? `${parts[0]}.xlsx` : cleanName;
                        const dateStr = parts[1] ? parts[1].trim() : '';

                        return (
                          <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ py: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111b21' }}>
                                {displayTitle}
                              </Typography>
                              {dateStr && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                  {dateStr}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5, fontWeight: 500 }}>{row.total}</TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#10b981', fontWeight: 500 }}>{row.sent}</TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#f59e0b', fontWeight: 500 }}>{row.pending}</TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#ef4444', fontWeight: 500 }}>
                              {(row.failed_send || 0) + (row.failed_delivery || 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#3b82f6', fontWeight: 500 }}>{row.replied || 0}</TableCell>
                          </TableRow>
                        );
                      })}
                      {(!stats.overall?.excelCampaigns || stats.overall.excelCampaigns.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            No hay campañas registradas en este rango de fechas.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              ) : (
                <StyledTableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Automatización / Plantilla</StyledTableCell>
                        <StyledTableCell align="right">Total</StyledTableCell>
                        <StyledTableCell align="right">Enviados</StyledTableCell>
                        <StyledTableCell align="right">Omitidos</StyledTableCell>
                        <StyledTableCell align="right">Fallidos</StyledTableCell>
                        <StyledTableCell align="right">Resp.</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.overall?.automatedCampaigns?.map((row, index) => {
                        const cleanName = (row.name || '').replace('Automatizacion:', '').trim();
                        return (
                          <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ py: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111b21' }}>
                                {cleanName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                Plantilla: {row.templateName || 'Desconocida'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5, fontWeight: 500 }}>{row.total}</TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#10b981', fontWeight: 500 }}>{row.sent}</TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#9ca3af', fontWeight: 500 }}>{row.skipped || 0}</TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#ef4444', fontWeight: 500 }}>
                              {(row.failed_send || 0) + (row.failed_delivery || 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5, color: '#3b82f6', fontWeight: 500 }}>{row.replied || 0}</TableCell>
                          </TableRow>
                        );
                      })}
                      {(!stats.overall?.automatedCampaigns || stats.overall.automatedCampaigns.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            No hay automatizaciones registradas en este rango de fechas.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              )}
            </Grid>

            {/* Table 5: Cost Breakdown Detail */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faCoins} style={{ color: '#008069' }} />
                Detalle de Costos
              </Typography>
              <StyledTableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Categoría</StyledTableCell>
                      <StyledTableCell align="right">Mensajes</StyledTableCell>
                      <StyledTableCell align="right">Costo (USD)</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.overall?.costs ? (
                      <>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 500, py: 1.5 }}>Marketing</TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>{stats.overall.costs.marketing?.count || 0}</TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, color: '#ef4444' }}>
                            ${stats.overall.costs.marketing?.cost?.toFixed(2) || '0.00'}
                          </TableCell>
                        </TableRow>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 500, py: 1.5 }}>Utility</TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>{stats.overall.costs.utility?.count || 0}</TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, color: '#f59e0b' }}>
                            ${stats.overall.costs.utility?.cost?.toFixed(2) || '0.00'}
                          </TableCell>
                        </TableRow>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 500, py: 1.5 }}>Authentication</TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>{stats.overall.costs.authentication?.count || 0}</TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, color: '#3b82f6' }}>
                            ${stats.overall.costs.authentication?.cost?.toFixed(2) || '0.00'}
                          </TableCell>
                        </TableRow>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 500, py: 1.5 }}>Service</TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>{stats.overall.costs.service?.count || 0}</TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, color: '#10b981' }}>
                            ${stats.overall.costs.service?.cost?.toFixed(2) || '0.00'}
                          </TableCell>
                        </TableRow>
                        <TableRow hover>
                          <TableCell sx={{ fontWeight: 500, py: 1.5 }}>Gratuitas (Free)</TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>{stats.overall.costs.free?.totalCount || 0}</TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, color: '#718096' }}>
                            $0.00
                          </TableCell>
                        </TableRow>
                        <TableRow hover sx={{ bgcolor: '#f8f9fa', '& td': { fontWeight: 700 } }}>
                          <TableCell sx={{ py: 1.5 }}>Total General</TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>
                            {(stats.overall.costs.marketing?.count || 0) +
                             (stats.overall.costs.utility?.count || 0) +
                             (stats.overall.costs.authentication?.count || 0) +
                             (stats.overall.costs.service?.count || 0) +
                             (stats.overall.costs.free?.totalCount || 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, color: '#008069' }}>
                            ${stats.overall.costs.totalCost?.toFixed(2) || '0.00'}
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No hay datos de costos.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>
              {stats.overall.costs?.free?.totalCount > 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e9edef' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#111b21', display: 'block', mb: 1.5 }}>
                    Desglose de Conversaciones Gratuitas:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(stats.overall.costs.free.byCategory || {}).map(([cat, count]) => (
                      <Chip
                        key={cat}
                        label={`Categoría ${cat.toUpperCase()}: ${count}`}
                        size="small"
                        sx={{ backgroundColor: '#f0f2f5', color: '#667085', fontSize: '11px', fontWeight: 500 }}
                      />
                    ))}
                    {Object.entries(stats.overall.costs.free.byType || {}).map(([type, count]) => (
                      <Chip
                        key={type}
                        label={`Tipo ${type.replace(/_/g, ' ').toUpperCase()}: ${count}`}
                        size="small"
                        sx={{ backgroundColor: '#e8f5e8', color: '#008069', fontSize: '11px', fontWeight: 500 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>

        </>
      )}
    </Box>
  );
}
